import { useState } from 'react';
import {
	Table,
	Tag,
	Switch,
	Button,
	Modal,
	Form,
	Input,
	InputNumber,
	Select,
	Tooltip,
	Space,
	Spin,
	notification,
} from 'antd';
import { PlusOutlined, EditOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
	useGetAdminStagingAreasQuery,
	useGetKitMachineGroupsQuery,
	useCreateStagingAreaMutation,
	useUpdateStagingAreaMutation,
} from '../../../redux/api/kit-staging';
import type {
	KitStagingAreaListItem,
	KitMachineGroup,
} from '../../../redux/api/kit-staging';
import RackEditorDrawer from './RackEditorDrawer';

interface FormValues {
	name: string;
	description: string;
	area_type: 'job_staging' | 'material_staging';
	route_order: number;
	machine_group_ids: number[];
	serves_all_machine_groups: boolean;
}

const StagingAreasTab = () => {
	const { data: areas, isLoading } = useGetAdminStagingAreasQuery();
	const { data: machineGroups = [] } = useGetKitMachineGroupsQuery();
	const [createArea, { isLoading: creating }] = useCreateStagingAreaMutation();
	const [updateArea, { isLoading: updating }] = useUpdateStagingAreaMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingArea, setEditingArea] = useState<KitStagingAreaListItem | null>(
		null
	);
	const [drawerAreaId, setDrawerAreaId] = useState<number | null>(null);
	const [form] = Form.useForm<FormValues>();

	const openCreateModal = () => {
		setEditingArea(null);
		form.resetFields();
		form.setFieldsValue({ area_type: 'job_staging', route_order: 0 });
		setModalOpen(true);
	};

	const openEditModal = (area: KitStagingAreaListItem) => {
		setEditingArea(area);
		form.setFieldsValue({
			name: area.name,
			description: area.description,
			area_type: area.area_type || 'job_staging',
			route_order: area.route_order ?? 0,
			machine_group_ids: area.machine_groups?.map((mg) => mg.id) || [],
			serves_all_machine_groups: area.serves_all_machine_groups ?? false,
		});
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
		setEditingArea(null);
		form.resetFields();
	};

	const handleToggleActive = async (
		area: KitStagingAreaListItem,
		checked: boolean
	) => {
		try {
			await updateArea({ id: area.id, body: { is_active: checked } }).unwrap();
			notification.success({
				message: `${area.name} ${checked ? 'activated' : 'deactivated'}`,
			});
		} catch {
			notification.error({ message: `Failed to update ${area.name}` });
		}
	};

	const handleSubmit = async (values: FormValues) => {
		try {
			const payload = {
				name: values.name,
				description: values.description,
				area_type: values.area_type || 'job_staging',
				route_order: values.route_order ?? 0,
				machine_group_ids: values.machine_group_ids || [],
				serves_all_machine_groups: !!values.serves_all_machine_groups,
			};
			if (editingArea) {
				await updateArea({ id: editingArea.id, body: payload }).unwrap();
				notification.success({ message: `Updated ${values.name}` });
			} else {
				await createArea(payload).unwrap();
				notification.success({ message: `Created ${values.name}` });
			}
			closeModal();
		} catch {
			notification.error({
				message: editingArea
					? 'Failed to update staging area'
					: 'Failed to create staging area',
			});
		}
	};

	const columns: ColumnsType<KitStagingAreaListItem> = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		{
			title: 'Type',
			dataIndex: 'area_type',
			key: 'area_type',
			width: 140,
			render: (val: string) => (
				<Tag color={val === 'material_staging' ? 'orange' : 'blue'}>
					{val === 'material_staging' ? 'Material Staging' : 'Job Staging'}
				</Tag>
			),
		},
		{
			title: 'Route Order',
			dataIndex: 'route_order',
			key: 'route_order',
			width: 100,
			align: 'center',
			sorter: (a, b) => (a.route_order ?? 0) - (b.route_order ?? 0),
		},
		{
			title: 'Machine Groups',
			dataIndex: 'machine_groups',
			key: 'machine_groups',
			render: (groups: KitMachineGroup[]) =>
				groups.map((mg) => (
					<Tag key={mg.id} color={mg.color}>
						{mg.name}
					</Tag>
				)),
		},
		{
			title: 'Racks',
			dataIndex: 'rack_count',
			key: 'racks',
			width: 80,
			align: 'center',
			render: (val: number) => val || '—',
		},
		{
			title: 'Shelves',
			dataIndex: 'total_shelves',
			key: 'total_shelves',
			width: 80,
			align: 'center',
		},
		{
			title: 'Active',
			dataIndex: 'is_active',
			key: 'is_active',
			width: 80,
			align: 'center',
			render: (active: boolean, record) => (
				<Tooltip
					title={
						active
							? 'Deactivate this staging area'
							: 'Activate this staging area'
					}
				>
					<Switch
						checked={active}
						onChange={(checked) => handleToggleActive(record, checked)}
						onClick={(_, e) => e.stopPropagation()}
					/>
				</Tooltip>
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Space>
					<Tooltip title="Edit">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								openEditModal(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Racks">
						<Button
							type="text"
							icon={<AppstoreOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								setDrawerAreaId(record.id);
							}}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-24">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="py-4">
			<div className="flex justify-end mb-4">
				<Tooltip title="Create a new staging area">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreateModal}
					>
						Add Staging Area
					</Button>
				</Tooltip>
			</div>

			<Table
				columns={columns}
				dataSource={areas || []}
				rowKey="id"
				pagination={false}
				size="middle"
			/>

			<Modal
				title={editingArea ? `Edit ${editingArea.name}` : 'Add Staging Area'}
				open={modalOpen}
				onCancel={closeModal}
				footer={null}
			>
				<Form<FormValues>
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{ area_type: 'job_staging', route_order: 0 }}
				>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, message: 'Please enter a name' }]}
					>
						<Input placeholder="e.g. Cell 1 Staging" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={2} placeholder="Optional description" />
					</Form.Item>
					<Form.Item
						name="area_type"
						label="Area Type"
						rules={[{ required: true }]}
					>
						<Select>
							<Select.Option value="job_staging">Job Staging</Select.Option>
							<Select.Option value="material_staging">
								Material Staging (Saw)
							</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item
						name="route_order"
						label="Route Order"
						tooltip="Physical shop-floor sequence for cart route. Lower numbers are visited first."
					>
						<InputNumber min={0} style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item name="machine_group_ids" label="Machine Groups">
						<Select
							mode="multiple"
							placeholder="Select machine groups"
							optionLabelProp="label"
						>
							{machineGroups.map((mg: KitMachineGroup) => (
								<Select.Option key={mg.id} value={mg.id} label={mg.name}>
									<Tag color={mg.color}>{mg.name}</Tag>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item
						name="serves_all_machine_groups"
						label="Serves all machine groups"
						valuePropName="checked"
						tooltip="When on, this area accepts cards from any machine group regardless of the list above. Site-mismatch checks are bypassed. Useful for a single MSA that stages material for the whole shop."
					>
						<Switch />
					</Form.Item>
					<Form.Item className="mb-0 flex justify-end">
						<Space>
							<Button onClick={closeModal}>Cancel</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={editingArea ? updating : creating}
							>
								{editingArea ? 'Save' : 'Create'}
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			<RackEditorDrawer
				areaId={drawerAreaId}
				open={drawerAreaId !== null}
				onClose={() => setDrawerAreaId(null)}
			/>
		</div>
	);
};

export default StagingAreasTab;
